import { useEffect, useState } from 'react'
import { useRestaurantStore, useRewardsStore, useAuthStore, useRewardsConfigMissStore, filterHiddenFromRewardConfig } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import type { MerchantRewardConfigResponse } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'
import { isShowcase } from '../showcase/showcase'


// Re-declared here to avoid cross-package import for a single type.
// Canonical copy also in WebRewardsPageService.ts.
/** Shape of Flask's GET /customer_rewards/{merchantId}/user/{userId} */
interface FlaskCustomerRewardResponse {
  id?: string
  current_points?: number
  currentPoints?: number
}

/**
 * Hook to fetch and populate rewards data from Flask API
 * Fetches merchant reward config for the current restaurant's business
 * Stores the full merchant config to preserve server-provided group structure
 *
 * ARCHITECTURE NOTE:
 * This hook is called at App.tsx level to pre-load rewards data.
 * Business logic for the rewards page lives in WebRewardsPageViewModel.
 * The ViewModel operates on the store data populated by this hook.
 */
export function useRewardsData() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { currentRestaurant } = useRestaurantStore()
  const { setMerchantConfig, setBalance, setCustomerRewardId } = useRewardsStore()
  const { user, isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (isShowcase()) return
    const fetchRewardsData = async () => {
      if (!currentRestaurant?.id) {
        safeGetLogger()?.debug('No restaurant ID, skipping rewards', { domain: 'reward' })
        return
      }

      const businessId = currentRestaurant.id

      // Rewards are opt-in and most tenants have none; a recent 404 for this
      // business is remembered per device so we don't re-probe Flask on every
      // app open (fleet-wide that was ~270 guaranteed-404 calls/day).
      const missStore = useRewardsConfigMissStore.getState()
      if (missStore.isKnownMissing(businessId)) {
        safeGetLogger()?.debug('Rewards config known missing, skipping fetch', { domain: 'reward', businessId })
        setMerchantConfig(null)
        setCustomerRewardId(null)
        return
      }

      safeGetLogger()?.debug('Fetching rewards', { domain: 'reward', businessId })

      setIsLoading(true)
      setError(null)

      try {
        // Call Flask endpoint to get merchant reward config by business ID
        const response = await apiClient.get<MerchantRewardConfigResponse>(
          `/merchant_reward_config/business/${businessId}`
        )

        safeGetLogger()?.debug('Reward config received', { domain: 'reward' })
        missStore.clearMiss(businessId)

        // Handle dollar-based redemption type by creating synthetic groups
        // Groups are generated dynamically based on user's balance in RewardsPage
        // Here we just store the base config - the page will generate tiers
        let configToStore = response

        if (response.redemption_type === 'dollar' &&
            response.point_value &&
            response.dollar_value &&
            (!response.item_redemption_groups || response.item_redemption_groups.length === 0)) {
          // For dollar redemption, we'll generate groups dynamically in RewardsPage
          // based on user's current balance (affordable tiers + 1)
          // Store a marker so RewardsPage knows to generate dollar tiers
          configToStore = {
            ...response,
            item_redemption_groups: [], // Empty - will be generated dynamically
          }

          safeGetLogger()?.debug('Dollar redemption config loaded', { domain: 'reward' })
        }

        // Store full merchant config to preserve group structure
        // Groups are accessed directly from merchantConfig.item_redemption_groups
        const filteredConfig = filterHiddenFromRewardConfig(configToStore)
        setMerchantConfig(filteredConfig)

        const groupCount = filteredConfig.item_redemption_groups?.length || 0
        safeGetLogger()?.reward('balance_viewed', { groupCount })

        // Fetch user's reward balance if authenticated
        // Use merchant_id from config (not business_id) as per API contract
        const merchantIds = response.merchant_id || []
        const merchantId = merchantIds[0]

        if (isAuthenticated && user?.id && merchantId) {
          try {
            safeGetLogger()?.debug('Fetching reward balance', { domain: 'reward', userId: user.id, merchantId })
            const balanceResponse = await apiClient.get<FlaskCustomerRewardResponse>(
              `/customer_rewards/${merchantId}/user/${user.id}`
            )
            const currentPoints = balanceResponse.current_points ?? balanceResponse.currentPoints ?? 0
            const customerRewardId = balanceResponse.id ?? null
            safeGetLogger()?.reward('balance_viewed', { points: currentPoints, customerRewardId })
            setBalance(currentPoints)
            setCustomerRewardId(customerRewardId)
          } catch (balanceErr) {
            // 404 means user has no reward record yet - that's OK
            safeGetLogger()?.debug('No reward record, defaulting to 0', { domain: 'reward' })
            setBalance(0)
            setCustomerRewardId(null)
          }
        } else if (isAuthenticated && user?.id && !merchantId) {
          safeGetLogger()?.warn('No merchantId in config', { domain: 'reward' })
          setBalance(0)
          setCustomerRewardId(null)
        }

      } catch (err) {
        const isNotFound = (err as { type?: string })?.type === 'notFound'
        if (isNotFound) {
          // Business has no rewards configured — legitimate state (rewards are manual opt-in)
          safeGetLogger()?.debug('No rewards configured for business', { domain: 'reward', businessId })
          missStore.recordMiss(businessId)
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load rewards'
          safeGetLogger()?.error('Rewards load failed', { domain: 'reward', businessId, error: errorMessage })
          setError(errorMessage)
        }
        // Clear merchant config and customer reward ID so tabs are hidden (both paths)
        setMerchantConfig(null)
        setCustomerRewardId(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchRewardsData()
  }, [currentRestaurant?.id, setMerchantConfig, setBalance, setCustomerRewardId, isAuthenticated, user?.id])

  return { isLoading, error }
}
